import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Calendar,
  ExternalLink,
  X,
  Loader2,
  Copy,
  ChevronDown,
  ChevronUp,
  Search,
  Filter,
  Tag,
  MessageSquare,
  Bell,
  MoreHorizontal,
  CheckSquare,
  ArrowRight,
  Trash2,
  Plus,
  Building2,
  Briefcase,
  User,
  CircleDot,
  Trophy,
  XCircle,
  Send,
  ClipboardCopy,
  Download,
  FileDown,
  FileText,
  ClipboardList,
  Inbox,
  Play,
  CheckCheck,
  BellRing,
  RefreshCw,
  Link2,
  Check,
  FileSignature,
  FileCheck
} from "lucide-react";
import AppSidebar from "@/components/AppSidebar";
import UsageWarningBanner from "@/components/UsageWarningBanner";
import SubmissionDrawer from "@/components/SubmissionDrawer";
import EmailComposerModal from "@/components/EmailComposerModal";

// Lead status configuration (pre_booking)
const leadStatusConfig = {
  new: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "New", icon: CircleDot },
  contacted: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Contacted", icon: Mail },
  qualified: { color: "bg-purple-100 text-purple-700 border-purple-200", label: "Qualified", icon: CheckCircle2 },
  won: { color: "bg-green-100 text-green-700 border-green-200", label: "Won", icon: Trophy },
  lost: { color: "bg-gray-100 text-gray-500 border-gray-200", label: "Lost", icon: XCircle }
};

// Intake status configuration - 3-PHASE WORKFLOW COMPLIANT
const intakeStatusConfig = {
  // INTAKE PHASE - Locked until Lead is Won
  intake_requested: { color: "bg-slate-100 text-slate-700 border-slate-200", label: "Intake Requested", icon: Send },
  intake_received: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Intake Received", icon: Inbox },
  awaiting_documents: { color: "bg-orange-100 text-orange-700 border-orange-200", label: "Awaiting Documents", icon: FileText },
  action_required: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Action Required", icon: AlertCircle },
  intake_completed: { color: "bg-teal-100 text-teal-700 border-teal-200", label: "Intake Completed", icon: CheckCircle2 }
};

// Service status configuration - Locked until Intake is Completed
const serviceStatusConfig = {
  in_review: { color: "bg-purple-100 text-purple-700 border-purple-200", label: "In Review", icon: ClipboardList },
  contract_signed: { color: "bg-pink-100 text-pink-700 border-pink-200", label: "Contract Signed", icon: FileCheck },
  scheduled: { color: "bg-cyan-100 text-cyan-700 border-cyan-200", label: "Scheduled", icon: Calendar },
  in_service: { color: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "In Service", icon: Play },
  completed: { color: "bg-green-100 text-green-700 border-green-200", label: "Completed", icon: CheckCheck }
};

// Combined config for lookups (backward compatibility)
const allStatusConfig = {
  ...leadStatusConfig,
  ...intakeStatusConfig,
  ...serviceStatusConfig,
  // Legacy mappings for backward compatibility
  awaiting_intake: { color: "bg-slate-100 text-slate-700 border-slate-200", label: "Intake Requested", icon: Send },
  client_action_required: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Action Required", icon: AlertCircle },
  needs_more_info: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Action Required", icon: AlertCircle }
};

// Legacy status mapping for backward compatibility
const legacyStatusMapping = {
  "waiting_for_intake": "intake_requested",
  "awaiting_intake": "intake_requested",
  "client_action_required": "action_required",
  "needs_more_info": "action_required"
};

const urgencyConfig = {
  "Priority": { color: "bg-red-100 text-red-700 border-red-200", label: "Priority", icon: AlertCircle },
  "High": { color: "bg-orange-100 text-orange-700 border-orange-200", label: "High", icon: Clock },
  "Moderate": { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Moderate", icon: Clock },
  "Low": { color: "bg-green-100 text-green-700 border-green-200", label: "Low", icon: CheckCircle2 },
  "Immediate": { color: "bg-red-100 text-red-700 border-red-200", label: "Priority", icon: AlertCircle },
  "Medium": { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Moderate", icon: Clock }
};

const getScoreColor = (score) => {
  if (score >= 90) return "score-high text-white";
  if (score >= 70) return "score-high text-white";
  if (score >= 50) return "score-medium text-white";
  return "score-low text-white";
};

const TAG_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
  "bg-orange-100 text-orange-700",
  "bg-cyan-100 text-cyan-700"
];

export default function SubmissionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Track if we've processed the URL parameter
  const [urlParamProcessed, setUrlParamProcessed] = useState(false);
  
  // Mode filter
  const [modeFilter, setModeFilter] = useState("all");
  
  // CRM State
  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Priority filter (for leads - filters by priority_label)
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [allTags, setAllTags] = useState([]);
  
  // Modal State
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newReminder, setNewReminder] = useState({ date: "", note: "" });
  const [newTag, setNewTag] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");
  
  // Email Templates State - keyed by mode
  const [emailTemplates, setEmailTemplates] = useState({ pre_booking: [], post_booking: [] });
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [schedulingLinks, setSchedulingLinks] = useState({});
  
  // Email Composer Modal State (for Submission Drawer email actions)
  const [emailComposerState, setEmailComposerState] = useState({
    open: false,
    submission: null,
    mode: "compose", // "compose" | "template"
    templateId: null // Selected template ID when using templates
  });
  
  // CSV Export State
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: "",
    endDate: "",
    status: "all",
    mode: "all"  // Export mode filter: all, pre_booking, post_booking
  });
  const [exporting, setExporting] = useState(false);
  
  // Won + Intake Confirmation Modal State
  const [wonIntakeModalOpen, setWonIntakeModalOpen] = useState(false);
  const [wonIntakeData, setWonIntakeData] = useState({
    submissionId: null,
    sendEmail: true,
    formId: "",
    recipientEmail: "",
    isResend: false  // Flag to distinguish resend from mark-as-won flow
  });
  const [wonIntakeLoading, setWonIntakeLoading] = useState(false);
  
  // Start Intake (Intake-First) Modal State
  const [startIntakeModalOpen, setStartIntakeModalOpen] = useState(false);
  const [startIntakeData, setStartIntakeData] = useState({
    contactId: "",
    name: "",
    email: "",
    formId: "",
    sendEmail: true,
    createNewContact: true
  });
  const [startIntakeLoading, setStartIntakeLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [leadForms, setLeadForms] = useState([]);  // pre_booking forms for Send Lead Form
  
  // Revert Warning Modal State (for Won → other status when intake sent)
  const [revertWarningOpen, setRevertWarningOpen] = useState(false);
  const [revertData, setRevertData] = useState({
    submissionId: null,
    newStatus: null
  });
  
  // Lost Reason Modal State
  const [lostReasonModalOpen, setLostReasonModalOpen] = useState(false);
  const [lostReasonData, setLostReasonData] = useState({
    submissionId: null,
    reason: "",
    otherText: ""
  });
  const [lostReasonSaving, setLostReasonSaving] = useState(false);
  
  // Client Link State (per-submission)
  const [clientLinkValue, setClientLinkValue] = useState("");
  const [clientLinkSaving, setClientLinkSaving] = useState(false);
  const [clientLinkCopied, setClientLinkCopied] = useState(false);
  
  // Use subscription context instead of local state
  const { subscriptionStatus } = useSubscription();

  useEffect(() => {
    fetchData(searchQuery);
    fetchTags();
    fetchEmailTemplates();
    fetchSchedulingLinks();
    fetchContacts();
    fetchIntakeForms();
    // No need to fetch subscription status - comes from context
  }, [modeFilter]);

  // Debounced search effect - triggers backend search as user types
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchData(searchQuery);
    }, 300); // 300ms debounce
    
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Sync client link when selected submission changes
  useEffect(() => {
    if (selectedSubmission) {
      setClientLinkValue(selectedSubmission.client_link || "");
    }
  }, [selectedSubmission?.id]);

  // Handle URL parameter to auto-select a specific submission (deep-link)
  useEffect(() => {
    // Wait until we have submissions loaded
    if (loading) return;
    
    const searchParams = new URLSearchParams(location.search);
    const submissionId = searchParams.get('id');
    
    // If there's a submission ID in the URL, try to select it
    if (submissionId && !urlParamProcessed) {
      setUrlParamProcessed(true);
      
      // First try to find it in the current list
      const submission = submissions.find(s => s.id === submissionId);
      if (submission) {
        setSelectedSubmission(submission);
        // Clear the URL parameter
        window.history.replaceState({}, '', '/submissions');
      } else {
        // Not in current list - fetch directly
        fetchSubmissionById(submissionId);
      }
    }
  }, [loading, location.search, submissions, urlParamProcessed]);

  // Fetch a specific submission by ID (for deep-linking)
  const fetchSubmissionById = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/leads/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSelectedSubmission(response.data);
        // Clear the URL parameter
        window.history.replaceState({}, '', '/submissions');
      }
    } catch (error) {
      console.error("Failed to fetch submission:", error);
      toast.error("Could not find the requested submission");
    }
  };

  // Refresh the currently selected submission (called after document status updates)
  const refreshSelectedSubmission = async () => {
    // Capture the ID before the async operation to avoid race conditions
    const currentId = selectedSubmission?.id;
    if (!currentId) return;
    
    try {
      const response = await axios.get(`${API}/leads/${currentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.id) {
        setSelectedSubmission(response.data);
        // Also update the submission in the list
        setSubmissions(prev => {
          if (!prev || !Array.isArray(prev)) return prev;
          return prev.map(s => {
            if (!s || !s.id) return s;
            return s.id === response.data.id ? response.data : s;
          });
        });
      }
    } catch (error) {
      console.error("Failed to refresh submission:", error);
    }
  };

  const fetchSchedulingLinks = async () => {
    try {
      const response = await axios.get(`${API}/business/scheduling-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulingLinks(response.data || {});
    } catch (error) {
      console.error("Failed to fetch scheduling links");
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data || []);
    } catch (error) {
      console.error("Failed to fetch contacts");
    }
  };

  const fetchIntakeForms = async () => {
    try {
      const response = await axios.get(`${API}/business/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend returns { forms: [...] }
      const allForms = response.data?.forms || response.data || [];
      // Filter to only post_booking (intake) forms that are published
      const intakeOnly = allForms.filter(f => 
        f.mode === "post_booking" && f.status !== "draft"
      );
      setIntakeForms(intakeOnly);
      
      // Filter to only pre_booking (lead) forms that are published
      const leadOnly = allForms.filter(f => 
        f.mode === "pre_booking" && f.status !== "draft"
      );
      setLeadForms(leadOnly);
      
      // Debug logging (can be removed after confirming fix)
      console.log("[fetchForms] Total forms returned:", allForms.length);
      console.log("[fetchForms] Intake forms (post_booking):", intakeOnly.length);
      console.log("[fetchForms] Lead forms (pre_booking):", leadOnly.length);
    } catch (error) {
      console.error("Failed to fetch forms:", error.response?.status, error.response?.data);
    }
  };

  const getFirstSchedulingLink = () => {
    if (schedulingLinks.google_calendar) return schedulingLinks.google_calendar;
    if (schedulingLinks.calendly) return schedulingLinks.calendly;
    if (schedulingLinks.square_appointments) return schedulingLinks.square_appointments;
    return null;
  };

  const fetchData = async (searchTerm = "") => {
    try {
      setLoading(true);
      let url = `${API}/submissions`;
      const params = new URLSearchParams();
      
      if (modeFilter !== "all") {
        params.append("mode", modeFilter);
      }
      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      const [submissionsRes, statsRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/submissions/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/leads/tags`, { headers: { Authorization: `Bearer ${token}` } });
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error("Failed to fetch tags");
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      // Fetch templates for both modes
      const [preBookingRes, postBookingRes] = await Promise.all([
        axios.get(`${API}/email/templates?mode=pre_booking`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/email/templates?mode=post_booking`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEmailTemplates({
        pre_booking: preBookingRes.data.templates || [],
        post_booking: postBookingRes.data.templates || []
      });
    } catch (error) {
      console.error("Failed to fetch email templates");
    }
  };

  // Get templates for the current submission mode
  const getTemplatesForSubmission = (submission) => {
    if (!submission) return [];
    const mode = submission.mode || "pre_booking";
    return emailTemplates[mode] || [];
  };

  // Get the current status based on mode
  // UNIFIED MODEL: For filtering purposes, we need to consider BOTH lead_stage and intake_stage
  const getSubmissionStatus = (submission) => {
    // This function is used for bucket placement based on mode context
    // For intake workflow (post_booking mode or intake_stage exists): use intake_stage
    // For lead pipeline (pre_booking mode): use lead_status
    
    // If we're explicitly in post_booking mode or filtering intakes, use intake_stage
    if (submission.mode === "post_booking" || submission.intake_stage) {
      // For intake workflow: use intake_stage (canonical) or fall back to intake_status (legacy)
      if (submission.intake_stage) {
        let status = submission.intake_stage;
        // Map legacy statuses to new canonical statuses
        if (legacyStatusMapping[status]) {
          status = legacyStatusMapping[status];
        }
        return status;
      }
      
      // Legacy fallback for post_booking mode
      let status = submission.intake_status || "awaiting_intake";
      if (legacyStatusMapping[status]) {
        status = legacyStatusMapping[status];
      }
      return status;
    }
    
    // For lead pipeline: use lead_status
    return submission.lead_status || submission.status || "new";
  };
  
  // Get the LEAD status specifically (ignores intake_stage)
  const getLeadStatus = (submission) => {
    return submission.lead_status || submission.status || null;
  };
  
  // Get the INTAKE status specifically
  const getIntakeStatus = (submission) => {
    if (submission.intake_stage) {
      let status = submission.intake_stage;
      if (legacyStatusMapping[status]) {
        status = legacyStatusMapping[status];
      }
      return status;
    }
    if (submission.mode === "post_booking") {
      let status = submission.intake_status || "awaiting_intake";
      if (legacyStatusMapping[status]) {
        status = legacyStatusMapping[status];
      }
      return status;
    }
    return null;
  };
  
  // Check if submission is in intake workflow (has intake_stage set)
  const isInIntakeWorkflow = (submission) => {
    return submission.intake_stage != null || submission.mode === "post_booking";
  };

  // Get status config based on mode - uses combined config for lookups
  const getStatusConfig = (submission) => {
    const status = getSubmissionStatus(submission);
    // Try to find in allStatusConfig which includes all phases and legacy mappings
    return allStatusConfig[status] || leadStatusConfig.new;
  };

  // Update submission status
  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      const submission = submissions.find(s => s.id === submissionId);
      
      // Determine if this is an intake status based on the new status value
      // Intake statuses: intake_requested, intake_received, awaiting_documents, action_required, intake_completed
      const INTAKE_STATUSES = ['intake_requested', 'intake_received', 'awaiting_documents', 'action_required', 'intake_completed'];
      const isIntakeStatus = INTAKE_STATUSES.includes(newStatus);
      const isInIntake = isInIntakeWorkflow(submission) || isIntakeStatus;
      
      // Build request body - clear lost_reason fields when moving away from lost
      const requestBody = { status: newStatus };
      if (submission?.lead_status === "lost" && newStatus !== "lost") {
        requestBody.lost_reason = null;
        requestBody.lost_reason_other = null;
      }
      
      // Use the old endpoint for backward compatibility
      await axios.put(`${API}/leads/${submissionId}/status`, requestBody, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local submissions state - use intake_stage for intake workflow
      const updatedFields = isInIntake 
        ? { intake_stage: newStatus, intake_status: newStatus } 
        : { lead_status: newStatus, status: newStatus };
      
      // Clear lost reason fields in local state when moving away from lost
      if (submission?.lead_status === "lost" && newStatus !== "lost") {
        updatedFields.lost_reason = null;
        updatedFields.lost_reason_other = null;
      }
      
      setSubmissions(prev => prev.filter(s => s && s.id).map(s => 
        s.id === submissionId ? { ...s, ...updatedFields } : s
      ));
      
      // Update selected submission if open
      if (selectedSubmission?.id === submissionId) {
        setSelectedSubmission(prev => ({ ...prev, ...updatedFields }));
      }
      
      // Refresh stats to update metric cards immediately
      const statsRes = await axios.get(`${API}/submissions/stats`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setStats(statsRes.data);
      
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Handle "Won" status click - opens confirmation modal for intake
  const handleWonClick = (submissionId) => {
    const submission = submissions.find(s => s.id === submissionId);
    if (!submission) return;
    
    setWonIntakeData({
      submissionId,
      sendEmail: true,
      formId: intakeForms.length > 0 ? intakeForms[0].id : "",
      recipientEmail: submission.customer_email || "",
      isResend: false  // This is the "Mark as Won" flow, not resend
    });
    setWonIntakeModalOpen(true);
  };

  // Confirm Won with optional intake OR Resend intake email
  const handleConfirmWonIntake = async () => {
    try {
      setWonIntakeLoading(true);
      
      // If this is a resend, just send the intake email (don't change status)
      if (wonIntakeData.isResend) {
        await handleSendIntakeEmail(
          wonIntakeData.submissionId,
          wonIntakeData.formId,
          wonIntakeData.recipientEmail
        );
        setWonIntakeModalOpen(false);
        return;
      }
      
      // Otherwise, this is the "Mark as Won" flow
      const response = await axios.patch(
        `${API}/submissions/${wonIntakeData.submissionId}`,
        {
          lead_stage: "won",
          start_intake: wonIntakeData.sendEmail,
          intake_form_id: wonIntakeData.formId,
          recipient_email: wonIntakeData.recipientEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSubmissions(prev => prev.filter(s => s && s.id).map(s => 
        s.id === wonIntakeData.submissionId 
          ? { 
              ...s, 
              lead_status: "won", 
              status: "won",
              intake_stage: wonIntakeData.sendEmail ? "intake_requested" : "awaiting_intake"
            }
          : s
      ));
      
      // Update selected submission if open
      if (selectedSubmission?.id === wonIntakeData.submissionId) {
        setSelectedSubmission(prev => ({
          ...prev,
          lead_status: "won",
          status: "won",
          intake_stage: wonIntakeData.sendEmail ? "intake_requested" : "awaiting_intake"
        }));
      }
      
      // Refresh data
      await fetchData();
      
      setWonIntakeModalOpen(false);
      toast.success(
        wonIntakeData.sendEmail 
          ? "Intake email sent — moved to Intake Requested" 
          : "Marked as Won — ready to start intake"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setWonIntakeLoading(false);
    }
  };

  // Handle "Start Intake" button click (intake-first flow)
  const handleStartIntakeClick = () => {
    setStartIntakeData({
      contactId: "",
      name: "",
      email: "",
      formId: intakeForms.length > 0 ? intakeForms[0].id : "",
      sendEmail: true,
      createNewContact: true
    });
    setStartIntakeModalOpen(true);
  };

  // Submit start intake (intake-first)
  const handleSubmitStartIntake = async () => {
    try {
      setStartIntakeLoading(true);
      
      // Validate required fields
      if (!startIntakeData.email) {
        toast.error("Email is required");
        return;
      }
      if (!startIntakeData.name && startIntakeData.createNewContact) {
        toast.error("Name is required");
        return;
      }
      if (!startIntakeData.formId) {
        toast.error("Please select an intake form");
        return;
      }
      
      const response = await axios.post(
        `${API}/submissions/start-intake`,
        {
          contact_id: startIntakeData.createNewContact ? null : startIntakeData.contactId,
          client_name: startIntakeData.name,
          client_email: startIntakeData.email,
          form_id: startIntakeData.formId,
          send_email: startIntakeData.sendEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data
      await fetchData();
      await fetchContacts();
      
      setStartIntakeModalOpen(false);
      toast.success(
        startIntakeData.sendEmail 
          ? "Intake email sent successfully" 
          : "Intake record created — ready to send"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start intake");
    } finally {
      setStartIntakeLoading(false);
    }
  };

  // Send intake for a submission that's in awaiting_intake
  const handleSendIntakeEmail = async (submissionId, formId, email) => {
    try {
      await axios.post(
        `${API}/submissions/${submissionId}/send-intake-email`,
        { form_id: formId, recipient_email: email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSubmissions(prev => prev.filter(s => s && s.id).map(s => 
        s.id === submissionId 
          ? { ...s, intake_stage: "intake_requested" }
          : s
      ));
      
      if (selectedSubmission?.id === submissionId) {
        setSelectedSubmission(prev => ({ ...prev, intake_stage: "intake_requested" }));
      }
      
      toast.success("Intake email sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send intake email");
    }
  };

  // Handle status change with Won reversion protection and Lost reason modal
  const handleStatusChange = (submissionId, newStatus) => {
    const submission = submissions.find(s => s.id === submissionId);
    if (!submission) return;
    
    // Check if changing status to "lost" - show lost reason modal
    if (newStatus === "lost" && submission.lead_status !== "lost") {
      setLostReasonData({ 
        submissionId, 
        reason: "", 
        otherText: "" 
      });
      setLostReasonModalOpen(true);
      return;
    }
    
    // Check if trying to revert from Won when intake was already sent
    if (submission.lead_status === "won" && 
        newStatus !== "won" && 
        submission.intake_stage === "intake_requested") {
      // Show warning modal
      setRevertData({ submissionId, newStatus });
      setRevertWarningOpen(true);
      return;
    }
    
    // Safe to revert (no intake sent) or moving to Won
    if (newStatus === "won" && submission.lead_status !== "won") {
      handleWonClick(submissionId);
    } else {
      updateSubmissionStatus(submissionId, newStatus);
    }
  };

  // Confirm lost reason and update status
  const handleConfirmLostReason = async () => {
    if (!lostReasonData.submissionId || !lostReasonData.reason) {
      toast.error("Please select a reason");
      return;
    }
    
    if (lostReasonData.reason === "other" && !lostReasonData.otherText.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    
    try {
      setLostReasonSaving(true);
      await axios.put(
        `${API}/leads/${lostReasonData.submissionId}/status`,
        { 
          lead_status: "lost",
          lost_reason: lostReasonData.reason,
          lost_reason_other: lostReasonData.reason === "other" ? lostReasonData.otherText : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSubmissions(prev => prev.filter(s => s && s.id).map(s => 
        s.id === lostReasonData.submissionId 
          ? { 
              ...s, 
              lead_status: "lost", 
              status: "lost",
              lost_reason: lostReasonData.reason,
              lost_reason_other: lostReasonData.reason === "other" ? lostReasonData.otherText : null
            } 
          : s
      ));
      
      if (selectedSubmission?.id === lostReasonData.submissionId) {
        setSelectedSubmission(prev => ({
          ...prev, 
          lead_status: "lost", 
          status: "lost",
          lost_reason: lostReasonData.reason,
          lost_reason_other: lostReasonData.reason === "other" ? lostReasonData.otherText : null
        }));
      }
      
      // Refresh stats to update metric cards immediately (same as regular status change)
      const statsRes = await axios.get(`${API}/submissions/stats`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setStats(statsRes.data);
      
      toast.success("Status updated to Lost");
      setLostReasonModalOpen(false);
      setLostReasonData({ submissionId: null, reason: "", otherText: "" });
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setLostReasonSaving(false);
    }
  };

  // Confirm revert from Won (user acknowledges intake was sent)
  const handleConfirmRevert = async () => {
    if (revertData.submissionId && revertData.newStatus) {
      await updateSubmissionStatus(revertData.submissionId, revertData.newStatus);
      setRevertWarningOpen(false);
      setRevertData({ submissionId: null, newStatus: null });
    }
  };

  // Filter submissions
  const filteredSubmissions = useMemo(() => {
    return submissions.filter(submission => {
      // Guard against null/undefined entries
      if (!submission || !submission.id) return false;
      
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        (submission.customer_name?.toLowerCase().includes(searchLower)) ||
        (submission.customer_email?.toLowerCase().includes(searchLower)) ||
        Object.values(submission.form_data || {}).some(val => 
          String(val).toLowerCase().includes(searchLower)
        );
      
      // Status filter - use the appropriate status based on mode context
      let matchesStatus = statusFilter === "all";
      
      if (!matchesStatus) {
        // Get current status of submission
        const currentStatus = getSubmissionStatus(submission);
        const leadStatus = getLeadStatus(submission);
        const intakeStatus = getIntakeStatus(submission);
        
        // Check if the filter matches any relevant status field
        // This handles the 3-phase workflow where statuses can be in different fields
        const allStatuses = [currentStatus, leadStatus, intakeStatus].filter(Boolean);
        matchesStatus = allStatuses.includes(statusFilter);
        
        // Special handling for service statuses which are stored in intake_stage
        const serviceStatuses = ["in_review", "contract_signed", "scheduled", "in_service", "completed"];
        if (serviceStatuses.includes(statusFilter)) {
          matchesStatus = intakeStatus === statusFilter;
        }
        
        // Handle legacy status mappings
        if (!matchesStatus && legacyStatusMapping[statusFilter]) {
          matchesStatus = allStatuses.includes(legacyStatusMapping[statusFilter]);
        }
      }
      
      // Priority filter (only for pre_booking - filters by priority_label)
      let matchesPriority = true;
      if (submission.mode !== "post_booking" && priorityFilter !== "all") {
        const priority = submission.priority_label || "";
        // Map filter values to actual priority labels
        switch (priorityFilter) {
          case "immediate": 
            matchesPriority = priority === "Immediate" || priority === "Priority"; 
            break;
          case "high": 
            matchesPriority = priority === "High"; 
            break;
          case "moderate": 
            matchesPriority = priority === "Moderate" || priority === "Medium"; 
            break;
          case "low": 
            matchesPriority = priority === "Low"; 
            break;
          default: 
            matchesPriority = true;
        }
      }
      
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [submissions, searchQuery, statusFilter, priorityFilter, modeFilter]);

  const getSubmissionName = (submission) => {
    if (submission.customer_name) return submission.customer_name;
    const nameField = Object.entries(submission.form_data || {}).find(([key]) => 
      key.toLowerCase().includes("name") && !key.toLowerCase().includes("company")
    );
    return nameField ? nameField[1] : "Unknown";
  };

  // Get display title for post_booking submissions with proper priority
  const getDisplayTitle = (submission) => {
    if (submission.mode !== "post_booking") {
      return getSubmissionName(submission);
    }
    
    const formData = submission.form_data || {};
    
    // Priority order for post_booking identity
    // 1. full_name from form_data
    const fullName = formData["Full Name"] || formData["full_name"] || formData["FullName"];
    if (fullName) return fullName;
    
    // 2. first_name + last_name
    const firstName = formData["First Name"] || formData["first_name"] || formData["FirstName"];
    const lastName = formData["Last Name"] || formData["last_name"] || formData["LastName"];
    if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();
    
    // 3. company/organization (check BEFORE customer_name for post_booking)
    const company = formData["Company Name"] || formData["company"] || formData["Company"] || 
                    formData["Company/Organization"] || formData["Organization"] || formData["organization"];
    if (company) return company;
    
    // 4. email (extract name part)
    const email = submission.customer_email || formData["Email"] || formData["email"];
    if (email) {
      const emailName = email.split("@")[0].replace(/[._]/g, " ");
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    // 5. project_name (as last resort before description)
    const projectName = formData["Project Name"] || formData["project_name"] || formData["ProjectName"];
    if (projectName) return projectName;
    
    // 6. project_description (truncate to 40 chars)
    const projectDesc = formData["Project Description"] || formData["project_description"] || 
                        formData["Description"] || formData["description"];
    if (projectDesc) {
      return projectDesc.length > 40 ? projectDesc.substring(0, 40) + "..." : projectDesc;
    }
    
    // 7. Fallback
    return "Client Intake";
  };

  // Get project info for post_booking submissions
  const getProjectInfo = (submission) => {
    if (submission.mode !== "post_booking") return null;
    
    const formData = submission.form_data || {};
    const projectName = formData["Project Name"] || formData["project_name"] || formData["ProjectName"];
    const projectDesc = formData["Project Description"] || formData["project_description"] || 
                        formData["Description"] || formData["description"];
    
    if (projectName) return projectName;
    if (projectDesc) {
      return projectDesc.length > 40 ? projectDesc.substring(0, 40) + "..." : projectDesc;
    }
    return null;
  };

  const getSubmissionEmail = (submission) => {
    if (submission.customer_email) return submission.customer_email;
    const emailField = Object.entries(submission.form_data || {}).find(([key]) => 
      key.toLowerCase().includes("email")
    );
    return emailField ? emailField[1] : null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Note handling
  const addNote = async () => {
    if (!newNote.trim() || !selectedSubmission) return;
    try {
      await axios.post(`${API}/leads/${selectedSubmission.id}/notes`, { content: newNote }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedNotes = [...(selectedSubmission.notes || []), { content: newNote, created_at: new Date().toISOString() }];
      setSelectedSubmission({ ...selectedSubmission, notes: updatedNotes });
      setSubmissions(submissions.filter(s => s && s.id).map(s => s.id === selectedSubmission.id ? { ...s, notes: updatedNotes } : s));
      setNewNote("");
      setNoteModalOpen(false);
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Tag handling
  const addTag = async () => {
    if (!newTag.trim() || !selectedSubmission) return;
    try {
      const updatedTags = [...new Set([...(selectedSubmission.tags || []), newTag.trim()])];
      await axios.put(`${API}/leads/${selectedSubmission.id}/tags`, { tags: updatedTags }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSubmission({ ...selectedSubmission, tags: updatedTags });
      setSubmissions(submissions.filter(s => s && s.id).map(s => s.id === selectedSubmission.id ? { ...s, tags: updatedTags } : s));
      if (!allTags.includes(newTag.trim())) {
        setAllTags([...allTags, newTag.trim()]);
      }
      setNewTag("");
      setTagModalOpen(false);
      toast.success("Tag added");
    } catch (error) {
      toast.error("Failed to add tag");
    }
  };

  const removeTag = async (tagToRemove) => {
    if (!selectedSubmission) return;
    try {
      const updatedTags = (selectedSubmission.tags || []).filter(t => t !== tagToRemove);
      await axios.put(`${API}/leads/${selectedSubmission.id}/tags`, { tags: updatedTags }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSubmission({ ...selectedSubmission, tags: updatedTags });
      setSubmissions(submissions.filter(s => s && s.id).map(s => s.id === selectedSubmission.id ? { ...s, tags: updatedTags } : s));
      toast.success("Tag removed");
    } catch (error) {
      toast.error("Failed to remove tag");
    }
  };

  // Client Link handling - auto-save on blur
  const handleClientLinkSave = async () => {
    if (!selectedSubmission) return;
    
    // Don't save if value hasn't changed
    if (clientLinkValue === (selectedSubmission.client_link || "")) return;
    
    try {
      setClientLinkSaving(true);
      await axios.put(
        `${API}/submissions/${selectedSubmission.id}/client-link`,
        { client_link: clientLinkValue || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSelectedSubmission(prev => ({ ...prev, client_link: clientLinkValue }));
      setSubmissions(submissions.filter(s => s && s.id).map(s => 
        s.id === selectedSubmission.id ? { ...s, client_link: clientLinkValue } : s
      ));
      
      toast.success("Client link saved");
    } catch (error) {
      toast.error("Failed to save client link");
    } finally {
      setClientLinkSaving(false);
    }
  };

  // Email handling - opens user's email client with template pre-filled
  const handleEmailSubmission = (submission, templateId = null) => {
    const email = getSubmissionEmail(submission);
    if (!email) {
      toast.error("No email address found");
      return;
    }
    
    if (templateId) {
      // Search for template in BOTH pre_booking (Lead) and post_booking (Intake) templates
      // since the dropdown shows all templates regardless of submission mode
      const allTemplates = [
        ...(emailTemplates.pre_booking || []),
        ...(emailTemplates.post_booking || [])
      ];
      const template = allTemplates.find(t => t.id === templateId);
      if (!template) {
        toast.error("Template not found");
        return;
      }
      
      // Replace template variables with actual values
      const customerName = submission.form_data?.["Full Name"] || 
                          submission.form_data?.["Name"] || 
                          submission.customer_name || 
                          "there";
      
      // Get first available scheduling link
      const schedulingLink = getFirstSchedulingLink() || "[Your scheduling link]";
      
      let subject = template.subject
        .replace(/\{customer_name\}/g, customerName)
        .replace(/\{\{first_name\}\}/g, customerName.split(' ')[0])
        .replace(/\{business_name\}/g, user?.business_name || "Our Team");
      
      let body = template.body
        .replace(/\{customer_name\}/g, customerName)
        .replace(/\{\{first_name\}\}/g, customerName.split(' ')[0])
        .replace(/\{business_name\}/g, user?.business_name || "Our Team")
        .replace(/\{\{scheduling_link\}\}/g, schedulingLink);
      
      // Create mailto link with subject and body
      const mailtoUrl = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoUrl;
      
      toast.success("Opening your email client...");
    } else {
      window.location.href = `mailto:${email}`;
    }
  };

  // CSV Export
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFilters.startDate) params.append("start_date", exportFilters.startDate);
      if (exportFilters.endDate) params.append("end_date", exportFilters.endDate);
      // Mode filter
      if (exportFilters.mode && exportFilters.mode !== "all") {
        params.append("mode", exportFilters.mode);
      }
      // Status filter - only append if not "all"
      if (exportFilters.status && exportFilters.status !== "all") {
        params.append("status", exportFilters.status);
      }
      
      const response = await axios.get(`${API}/leads/export/csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `submissions_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setExportModalOpen(false);
      toast.success("Export completed");
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Get available statuses for export modal based on selected mode
  const getExportStatusOptions = () => {
    if (exportFilters.mode === "pre_booking") {
      return { type: "single", statuses: leadStatusConfig, label: "Lead Statuses" };
    }
    if (exportFilters.mode === "post_booking") {
      return { 
        type: "grouped", 
        groups: [
          { label: "Intake Phase", statuses: intakeStatusConfig },
          { label: "Service Phase", statuses: serviceStatusConfig }
        ]
      };
    }
    // Mode "all" - show all 3 phases grouped
    return { 
      type: "grouped", 
      groups: [
        { label: "Lead Phase", statuses: leadStatusConfig },
        { label: "Intake Phase", statuses: intakeStatusConfig },
        { label: "Service Phase", statuses: serviceStatusConfig }
      ]
    };
  };

  // Get available statuses based on current mode filter
  const getAvailableStatuses = () => {
    if (modeFilter === "post_booking") {
      // Intake mode - show both Intake and Service phases
      return { 
        type: "grouped", 
        groups: [
          { label: "Intake Phase", statuses: intakeStatusConfig, color: "text-purple-600" },
          { label: "Service Phase", statuses: serviceStatusConfig, color: "text-emerald-600" }
        ]
      };
    }
    if (modeFilter === "pre_booking") {
      return { type: "single", statuses: leadStatusConfig };
    }
    // All mode - return all 3 phases grouped
    return { 
      type: "grouped", 
      groups: [
        { label: "Lead Phase", statuses: leadStatusConfig, color: "text-blue-600" },
        { label: "Intake Phase", statuses: intakeStatusConfig, color: "text-purple-600" },
        { label: "Service Phase", statuses: serviceStatusConfig, color: "text-emerald-600" }
      ]
    };
  };

  // Get mode display label
  const getModeLabel = () => {
    if (modeFilter === "pre_booking") return "Leads";
    if (modeFilter === "post_booking") return "Intakes";
    return "All";
  };

  // Get mode icon color class
  const getModeColor = () => {
    if (modeFilter === "pre_booking") return "text-blue-600";
    if (modeFilter === "post_booking") return "text-purple-600";
    return "text-muted-foreground";
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="submissions-page">
      <AppSidebar user={user} onLogout={logout} />
      
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* SLICE 5: Usage Warning Banner */}
          <UsageWarningBanner />
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold" data-testid="submissions-title">Submissions</h1>
              <p className="text-muted-foreground mt-1">Manage your leads and client intakes</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setExportModalOpen(true)}
                className="rounded-xl"
                data-testid="export-csv-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                onClick={handleStartIntakeClick}
                className="rounded-xl bg-primary hover:bg-primary/90"
                data-testid="start-intake-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start Intake
              </Button>
            </div>
          </div>

          {/* Mode Filter Dropdown - Replaces Tabs */}
          <Card className="rounded-2xl border-none shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Mode Filter - First */}
                <Select value={modeFilter} onValueChange={(value) => {
                  setModeFilter(value);
                  setStatusFilter("all"); // Reset status filter when mode changes
                  setPriorityFilter("all"); // Reset priority filter when mode changes
                }}>
                  <SelectTrigger className="w-40 rounded-xl" data-testid="mode-filter">
                    <div className="flex items-center gap-2">
                      <Filter className={`w-4 h-4 ${getModeColor()}`} />
                      <SelectValue placeholder="All" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="flex items-center gap-2">All</span>
                    </SelectItem>
                    <SelectItem value="pre_booking">
                      <span className="flex items-center gap-2 text-blue-600">Leads</span>
                    </SelectItem>
                    <SelectItem value="post_booking">
                      <span className="flex items-center gap-2 text-purple-600">Intakes</span>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Dynamic Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-52 rounded-xl" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {(() => {
                      const available = getAvailableStatuses();
                      if (available.type === "grouped") {
                        // Grouped mode (All selected)
                        return available.groups.map((group, idx) => (
                          <div key={group.label}>
                            {idx > 0 && <DropdownMenuSeparator className="my-1" />}
                            <div className={`px-2 py-1.5 text-xs font-semibold ${group.color} uppercase tracking-wide`}>
                              — {group.label} —
                            </div>
                            {Object.entries(group.statuses).map(([key, config]) => (
                              <SelectItem key={key} value={key}>
                                <span className="flex items-center gap-2">
                                  <config.icon className="w-3 h-3" />
                                  {config.label}
                                </span>
                              </SelectItem>
                            ))}
                          </div>
                        ));
                      } else {
                        // Single mode (Leads or Intakes selected)
                        return Object.entries(available.statuses).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-2">
                              <config.icon className="w-3 h-3" />
                              {config.label}
                            </span>
                          </SelectItem>
                        ));
                      }
                    })()}
                  </SelectContent>
                </Select>

                {/* Priority Filter - Only show for Leads mode */}
                {modeFilter === "pre_booking" && (
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="w-44 rounded-xl" data-testid="priority-filter">
                      <SelectValue placeholder="All Priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="immediate">
                        <span className="flex items-center gap-2">
                          <AlertCircle className="w-3 h-3 text-red-500" />
                          Immediate
                        </span>
                      </SelectItem>
                      <SelectItem value="high">
                        <span className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-orange-500" />
                          High
                        </span>
                      </SelectItem>
                      <SelectItem value="moderate">
                        <span className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-yellow-500" />
                          Moderate
                        </span>
                      </SelectItem>
                      <SelectItem value="low">
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                          Low
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search submissions, emails, or answers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-xl"
                    data-testid="search-submissions-input"
                  />
                </div>
                
                {/* Clear All Button - Only show if any filter is active */}
                {(modeFilter !== "all" || statusFilter !== "all" || priorityFilter !== "all" || searchQuery) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setModeFilter("all");
                      setStatusFilter("all");
                      setPriorityFilter("all");
                      setSearchQuery("");
                    }}
                    className="text-muted-foreground hover:text-foreground whitespace-nowrap"
                    data-testid="clear-all-filters"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Subscription Limit Warning */}
          {(() => {
            if (!subscriptionStatus) return null;
            const leadsUsed = subscriptionStatus.leads_used_current_period || 0;
            const leadsLimit = subscriptionStatus.leads_limit || 100;
            const percentage = Math.round((leadsUsed / leadsLimit) * 100);
            const isAlmostFull = percentage >= 80;
            const isAtLimit = percentage >= 100;
            const remaining = Math.max(0, leadsLimit - leadsUsed);
            
            if (percentage >= 75) {
              return (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 p-4 rounded-2xl border ${
                    isAtLimit 
                      ? 'bg-red-50 border-red-200' 
                      : isAlmostFull 
                        ? 'bg-orange-50 border-orange-200' 
                        : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertCircle className={`w-5 h-5 ${
                        isAtLimit ? 'text-red-500' : isAlmostFull ? 'text-orange-500' : 'text-yellow-500'
                      }`} />
                      <div>
                        <p className={`font-semibold ${
                          isAtLimit ? 'text-red-800' : isAlmostFull ? 'text-orange-800' : 'text-yellow-800'
                        }`}>
                          {isAtLimit 
                            ? 'Submission limit reached!' 
                            : isAlmostFull 
                              ? `Only ${remaining} submission${remaining !== 1 ? 's' : ''} remaining` 
                              : `${percentage}% of your ${subscriptionStatus.is_trialing ? 'trial' : 'monthly'} limit used`
                          }
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {isAtLimit
                            ? 'New form submissions will be blocked until you upgrade.'
                            : `You've used ${leadsUsed} of ${leadsLimit} submissions this ${subscriptionStatus.is_trialing ? 'trial period' : 'month'}.`
                          }
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/settings?tab=billing')} 
                      size="sm"
                      className={`rounded-full ${
                        isAtLimit 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : isAlmostFull 
                            ? 'bg-orange-600 hover:bg-orange-700' 
                            : 'bg-yellow-600 hover:bg-yellow-700'
                      }`}
                    >
                      {isAtLimit ? 'Upgrade Now' : 'View Plans'}
                    </Button>
                  </div>
                </motion.div>
              );
            }
            return null;
          })()}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Stats Grid - Clean Mode Separation */}
              
              {/* Lead Metrics - Blue System */}
              {(modeFilter === "all" || modeFilter === "pre_booking") && (
                <div className="mb-6">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                    Lead Phase
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { label: "Total Leads", value: stats?.pre_booking?.total || 0, icon: Users, color: "bg-blue-100 text-blue-600", filter: null },
                      { label: "New", value: stats?.pre_booking?.new || 0, icon: CircleDot, color: "bg-blue-50 text-blue-500", filter: "new" },
                      { label: "Contacted", value: stats?.pre_booking?.contacted || 0, icon: Mail, color: "bg-yellow-100 text-yellow-600", filter: "contacted" },
                      { label: "Qualified", value: stats?.pre_booking?.qualified || 0, icon: CheckCircle2, color: "bg-purple-100 text-purple-600", filter: "qualified" },
                      { label: "Won", value: stats?.pre_booking?.won || 0, icon: Trophy, color: "bg-green-100 text-green-600", filter: "won" },
                      { label: "Lost", value: stats?.pre_booking?.lost || 0, icon: XCircle, color: "bg-gray-100 text-gray-500", filter: "lost" }
                    ].map((stat, index) => {
                      const isActive = stat.filter !== null && statusFilter === stat.filter;
                      return (
                        <motion.div
                          key={`lead-${stat.label}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Card 
                            className={`rounded-xl border-none shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${isActive && modeFilter === "pre_booking" ? "ring-2 ring-blue-400 ring-offset-2" : ""}`}
                            onClick={() => {
                              if (modeFilter === "all") setModeFilter("pre_booking");
                              if (stat.filter) setStatusFilter(stat.filter);
                              else setStatusFilter("all");
                            }}
                            data-testid={`stat-card-lead-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <CardContent className="p-3 flex items-center gap-2">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat.color}`}>
                                <stat.icon className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-xl font-bold">{stat.value}</p>
                                <p className="text-[10px] text-muted-foreground leading-tight">{stat.label}</p>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Intake Metrics - Purple System */}
              {(modeFilter === "all" || modeFilter === "post_booking") && (
                <div className="mb-6 space-y-5">
                  {/* Intake Phase Row - 6 cards */}
                  <div>
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                      Intake Phase
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {[
                        { label: "Total Intakes", value: stats?.post_booking?.total || 0, icon: ClipboardList, color: "bg-purple-100 text-purple-600", filter: null },
                        { label: "Requested", value: stats?.post_booking?.intake_requested || 0, icon: Clock, color: "bg-slate-100 text-slate-600", filter: "intake_requested" },
                        { label: "Received", value: stats?.post_booking?.received || 0, icon: Inbox, color: "bg-purple-50 text-purple-500", filter: "intake_received" },
                        { label: "Awaiting Docs", value: stats?.post_booking?.awaiting_documents || 0, icon: FileText, color: "bg-orange-100 text-orange-600", filter: "awaiting_documents" },
                        { label: "Action Required", value: stats?.post_booking?.action_required_total || 0, icon: AlertCircle, color: "bg-amber-100 text-amber-600", filter: "action_req_group" },
                        { label: "Intake Complete", value: stats?.post_booking?.intake_completed || 0, icon: CheckCircle2, color: "bg-emerald-100 text-emerald-600", filter: "intake_completed" }
                      ].map((stat, index) => {
                        const isActive = stat.filter !== null && statusFilter === stat.filter;
                        return (
                          <motion.div
                            key={`intake-${stat.label}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                          >
                            <Card 
                              className={`rounded-xl border-none shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${isActive && modeFilter === "post_booking" ? "ring-2 ring-purple-400 ring-offset-2" : ""}`}
                              onClick={() => {
                                if (modeFilter === "all") setModeFilter("post_booking");
                                if (stat.filter) setStatusFilter(stat.filter);
                                else setStatusFilter("all");
                              }}
                              data-testid={`stat-card-intake-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <CardContent className="p-3 flex items-center gap-2">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat.color}`}>
                                  <stat.icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-xl font-bold">{stat.value}</p>
                                  <p className="text-[10px] text-muted-foreground leading-tight">{stat.label}</p>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Service Phase Row - 5 cards */}
                  <div>
                    <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                      Service Phase
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {[
                        { label: "Total Service", value: stats?.post_booking?.total_service || 0, icon: Briefcase, color: "bg-green-100 text-green-600", filter: null },
                        { label: "Contract Signed", value: stats?.post_booking?.contract_signed || 0, icon: FileSignature, color: "bg-blue-100 text-blue-600", filter: "contract_signed" },
                        { label: "Scheduled", value: stats?.post_booking?.scheduled || 0, icon: Calendar, color: "bg-cyan-100 text-cyan-600", filter: "scheduled" },
                        { label: "In Service", value: stats?.post_booking?.in_service || 0, icon: Play, color: "bg-indigo-100 text-indigo-600", filter: "in_service" },
                        { label: "Service Complete", value: stats?.post_booking?.service_completed || 0, icon: CheckCheck, color: "bg-emerald-100 text-emerald-700", filter: "service_completed" }
                      ].map((stat, index) => {
                        const isActive = stat.filter !== null && statusFilter === stat.filter;
                        return (
                          <motion.div
                            key={`service-${stat.label}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                          >
                            <Card 
                              className={`rounded-xl border-none shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${isActive && modeFilter === "post_booking" ? "ring-2 ring-green-400 ring-offset-2" : ""}`}
                              onClick={() => {
                                if (modeFilter === "all") setModeFilter("post_booking");
                                if (stat.filter) setStatusFilter(stat.filter);
                                else setStatusFilter("all");
                              }}
                              data-testid={`stat-card-service-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <CardContent className="p-3 flex items-center gap-2">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat.color}`}>
                                  <stat.icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-xl font-bold">{stat.value}</p>
                                  <p className="text-[10px] text-muted-foreground leading-tight">{stat.label}</p>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Submissions List */}
              <Card className="rounded-2xl border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">
                      {modeFilter === "post_booking" ? "Intakes" : modeFilter === "pre_booking" ? "Leads" : "Submissions"} ({filteredSubmissions.length})
                    </h3>
                  </div>

                  {filteredSubmissions.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      {searchQuery ? (
                        <>
                          <h3 className="text-lg font-medium mb-2">No results found</h3>
                          <p className="text-muted-foreground">Try searching by name, email, or keywords from your submissions</p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-lg font-medium mb-2">No submissions yet</h3>
                          <p className="text-muted-foreground">Share your intake form to start receiving submissions</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredSubmissions.map((submission, index) => {
                        const statusConfig = getStatusConfig(submission);
                        const StatusIcon = statusConfig.icon;
                        const isPreBooking = submission.mode !== "post_booking";
                        
                        return (
                          <motion.div
                            key={submission.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className="group p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-muted/30 transition-all cursor-pointer"
                            onClick={() => setSelectedSubmission(submission)}
                            data-testid={`submission-row-${submission.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {/* Lifecycle Type Badge - Based on intake_stage */}
                                {(() => {
                                  // Lifecycle rule: "Intake" when lead_status === "won" AND has intake_stage
                                  // This matches the logic in SubmissionDrawer
                                  const hasIntakePhase = submission.lead_status === "won" && submission.intake_stage;
                                  const isIntakeReceived = submission.intake_stage === "intake_received";
                                  const lifecycleType = hasIntakePhase || submission.mode === "post_booking" ? "Intake" : "Lead";
                                  
                                  // Helper to derive score from level when actual score is missing
                                  const deriveScoreFromLevel = (level) => {
                                    if (!level) return null;
                                    const levelLower = level.toLowerCase();
                                    if (levelLower === 'high' || levelLower === 'excellent') return 75;
                                    if (levelLower === 'medium' || levelLower === 'good' || levelLower === 'fair') return 50;
                                    if (levelLower === 'low' || levelLower === 'poor') return 25;
                                    return null;
                                  };
                                  
                                  // Primary and secondary scores based on lifecycle
                                  const leadScoreLevel = submission.lead_score_level ?? submission.priority_label;
                                  const rawLeadScore = submission.lead_score ?? submission.ai_score;
                                  const leadScore = rawLeadScore != null ? Math.ceil(rawLeadScore) : deriveScoreFromLevel(leadScoreLevel);
                                  
                                  const rawIntakeScore = submission.intake_score ?? submission.intake_ai_v1?.intake_quality?.quality_score;
                                  const intakeScoreLevel = submission.intake_score_level;
                                  const intakeScore = rawIntakeScore != null ? Math.ceil(rawIntakeScore) : deriveScoreFromLevel(intakeScoreLevel);
                                  
                                  // Primary score based on lifecycle
                                  const primaryScore = hasIntakePhase ? (intakeScore ?? leadScore) : leadScore;
                                  
                                  return (
                                    <>
                                      <Badge 
                                        variant="outline" 
                                        className={`text-xs ${lifecycleType === 'Intake' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}
                                      >
                                        {lifecycleType}
                                      </Badge>
                                      
                                      {/* Primary Score Display */}
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                                              primaryScore === null || primaryScore === undefined ? 'bg-slate-100 text-slate-400' :
                                              primaryScore >= 75 ? 'bg-green-100 text-green-700' :
                                              primaryScore >= 50 ? 'bg-amber-100 text-amber-700' :
                                              'bg-red-100 text-red-700'
                                            }`}>
                                              {primaryScore === null || primaryScore === undefined ? "—" : primaryScore}
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{lifecycleType === 'Intake' ? 'Intake Clarity Score' : 'Lead Score'}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                      
                                      {/* Secondary Score (if intake received, show lead score as secondary) */}
                                      {isIntakeReceived && (leadScore !== null) && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="text-xs text-muted-foreground bg-slate-50 px-2 py-1 rounded">
                                                Lead: {leadScore}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Original Lead Score (historical)</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                      
                                      {/* Intake Pending indicator (if intake requested but not received) */}
                                      {!isIntakeReceived && submission.intake_stage === "intake_requested" && (
                                        <span className="text-xs text-muted-foreground">
                                          Intake: Pending
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                                
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold">{getDisplayTitle(submission)}</p>
                                    {isPreBooking && submission.priority_label && (
                                      <Badge className={`${urgencyConfig[submission.priority_label]?.color || 'bg-gray-100 text-gray-700'} text-xs`}>
                                        {submission.priority_label}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {getSubmissionEmail(submission) || "No email"}
                                  </p>
                                  {/* Project info for post_booking - subtle muted line */}
                                  {!isPreBooking && getProjectInfo(submission) && (
                                    <p className="text-xs text-muted-foreground/70 mt-0.5">
                                      Project: {getProjectInfo(submission)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4">
                                {/* Tags */}
                                <div className="hidden md:flex gap-1">
                                  {(submission.tags || []).slice(0, 2).map((tag, idx) => (
                                    <Badge key={tag} className={`${TAG_COLORS[idx % TAG_COLORS.length]} text-xs`}>
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                                
                                {/* Dual Status Badges - Show both lead_stage and intake_stage when both exist */}
                                <div className="flex items-center gap-2">
                                  {/* Lead Stage Badge (always show for leads, show Won for converted) */}
                                  {(submission.lead_status || submission.status) && (
                                    <Badge 
                                      className={`${leadStatusConfig[submission.lead_status || submission.status]?.color || 'bg-gray-100 text-gray-600'} flex items-center gap-1 text-xs`}
                                      variant="outline"
                                    >
                                      {React.createElement(leadStatusConfig[submission.lead_status || submission.status]?.icon || Clock, { className: "w-3 h-3" })}
                                      {leadStatusConfig[submission.lead_status || submission.status]?.label || submission.lead_status || submission.status}
                                    </Badge>
                                  )}
                                  
                                  {/* Intake Stage Badge (show when intake workflow is active) */}
                                  {submission.intake_stage && (
                                    <Badge 
                                      className={`${intakeStatusConfig[submission.intake_stage]?.color || 'bg-purple-100 text-purple-600'} flex items-center gap-1 text-xs`}
                                    >
                                      {React.createElement(intakeStatusConfig[submission.intake_stage]?.icon || ClipboardList, { className: "w-3 h-3" })}
                                      {intakeStatusConfig[submission.intake_stage]?.label || submission.intake_stage}
                                    </Badge>
                                  )}
                                  
                                  {/* Fallback: Show single status if neither dual-stage scenario */}
                                  {!submission.lead_status && !submission.status && !submission.intake_stage && (
                                    <Badge className={`${statusConfig.color} flex items-center gap-1`}>
                                      <StatusIcon className="w-3 h-3" />
                                      {statusConfig.label}
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Reminder Sent Indicator (post_booking only) */}
                                {!isPreBooking && submission.reminder?.sent_at && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center text-amber-500" data-testid="reminder-sent-indicator">
                                          <BellRing className="w-4 h-4" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        <p>Reminder sent {new Date(submission.reminder.sent_at).toLocaleDateString()}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                
                                {/* Date */}
                                <span className="text-sm text-muted-foreground hidden lg:block">
                                  {formatDate(submission.created_at)}
                                </span>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>

      {/* Submission Detail Drawer - REDESIGNED */}
      <SubmissionDrawer
        open={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        submission={selectedSubmission}
        token={token}
        onStatusChange={handleStatusChange}
        onUpdateSubmission={(updated) => {
          if (!updated || !updated.id) {
            console.error("Invalid update received in onUpdateSubmission");
            return;
          }
          setSelectedSubmission(updated);
          setSubmissions(prev => prev.filter(s => s && s.id).map(s => s.id === updated.id ? updated : s));
        }}
        onRefresh={refreshSelectedSubmission}
        emailTemplates={emailTemplates}
        intakeForms={intakeForms}
        leadForms={leadForms}
        onSendIntake={(submission) => {
          // Check if this is a resend (already in intake phases)
          const intakeStage = submission.intake_stage || submission.intake_status;
          const isResend = ["intake_requested", "intake_received", "awaiting_documents", "action_required", "client_action_required"].includes(intakeStage);
          
          setWonIntakeData({
            submissionId: submission.id,
            sendEmail: true,
            formId: intakeForms.length > 0 ? intakeForms[0].id : "",
            recipientEmail: submission.customer_email || "",
            isResend: isResend
          });
          setWonIntakeModalOpen(true);
        }}
        onSendEmail={(submission, templateIdOrMode) => {
          // templateIdOrMode can be:
          // - "compose" for compose button
          // - A template ID (string) when a specific template is selected
          const isTemplateId = templateIdOrMode && templateIdOrMode !== "compose" && templateIdOrMode !== "template";
          
          console.log("[EmailTemplate] onSendEmail called:", { templateIdOrMode, isTemplateId });
          
          setEmailComposerState({
            open: true,
            submission: submission,
            mode: isTemplateId ? "template" : "compose",
            templateId: isTemplateId ? templateIdOrMode : null
          });
        }}
        onRequestDocuments={async (submission) => {
          try {
            const res = await axios.post(
              `${API}/leads/${submission.id}/request-documents`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Update local state
            const updatedFields = {
              intake_stage: "awaiting_documents",
              intake_status: "awaiting_documents"
            };
            setSubmissions(prev => prev.map(s => 
              s.id === submission.id ? { ...s, ...updatedFields } : s
            ));
            if (selectedSubmission?.id === submission.id) {
              setSelectedSubmission(prev => ({ ...prev, ...updatedFields }));
            }
            
            // Show appropriate message
            if (res.data.email_sent) {
              toast.success(`Document request sent to ${submission.customer_email}`);
            } else {
              toast.error(res.data.email_error || "Failed to send email");
            }
            
            // Refresh stats
            const statsRes = await axios.get(`${API}/submissions/stats`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setStats(statsRes.data);
          } catch (err) {
            console.error("Failed to request documents:", err);
            toast.error(err.response?.data?.detail || "Failed to request documents");
          }
        }}
        onSendForSignature={async (submission) => {
          // Send uploaded document for e-signature via SignWell
          console.log("[E-Signature] Sending document for signature:", submission.id);
          
          // Validate document exists
          if (!submission.esign_document_name && !submission.signwell_document_path) {
            toast.error("No document uploaded. Please upload a document first.");
            return;
          }
          
          try {
            const res = await axios.post(
              `${API}/submissions/${submission.id}/send-for-signature`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (res.data.status === "success") {
              toast.success("Document sent for signature!");
              
              // Update local state with SignWell data
              const updatedFields = {
                signwell_status: res.data.signwell_status || "pending",
                signwell_document_id: res.data.document_id,
                signwell_signing_url: res.data.signing_url
              };
              
              setSubmissions(prev => prev.map(s => 
                s.id === submission.id ? { ...s, ...updatedFields } : s
              ));
              if (selectedSubmission?.id === submission.id) {
                setSelectedSubmission(prev => ({ ...prev, ...updatedFields }));
              }
              
              // Refresh selected submission to get full updated data
              refreshSelectedSubmission();
            }
          } catch (err) {
            console.error("[E-Signature] Failed to send:", err);
            toast.error(err.response?.data?.detail || "Failed to send document for signature");
          }
        }}
      />

      {/* Note Modal */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Enter your note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteModalOpen(false)}>Cancel</Button>
            <Button onClick={addNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Modal */}
      <Dialog open={tagModalOpen} onOpenChange={setTagModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Enter new tag..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
            />
            {allTags.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Existing tags</p>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <Badge 
                      key={tag} 
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => setNewTag(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagModalOpen(false)}>Cancel</Button>
            <Button onClick={addTag}>Add Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date & Time</Label>
              <Input
                type="datetime-local"
                value={newReminder.date}
                onChange={(e) => setNewReminder({ ...newReminder, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                placeholder="Reminder note..."
                value={newReminder.note}
                onChange={(e) => setNewReminder({ ...newReminder, note: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success("Reminder set");
              setReminderModalOpen(false);
            }}>Set Reminder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Submissions</DialogTitle>
            <DialogDescription>Download your submissions as a CSV file</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={exportFilters.startDate}
                  onChange={(e) => setExportFilters({ ...exportFilters, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={exportFilters.endDate}
                  onChange={(e) => setExportFilters({ ...exportFilters, endDate: e.target.value })}
                />
              </div>
            </div>
            {/* Mode Filter */}
            <div>
              <Label>Submission Type</Label>
              <Select 
                value={exportFilters.mode} 
                onValueChange={(v) => setExportFilters({ ...exportFilters, mode: v, status: "all" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types (Leads + Intakes)</SelectItem>
                  <SelectItem value="pre_booking">Leads Only</SelectItem>
                  <SelectItem value="post_booking">Intakes Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Status Filter - Mode Aware */}
            <div>
              <Label>Status Filter</Label>
              <Select 
                value={exportFilters.status} 
                onValueChange={(v) => setExportFilters({ ...exportFilters, status: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {(() => {
                    const options = getExportStatusOptions();
                    if (options.type === "single") {
                      return Object.entries(options.statuses).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ));
                    } else {
                      // Grouped display for "all" mode
                      return options.groups.flatMap(group => [
                        <SelectItem key={`group-${group.label}`} value={`__group_${group.label}`} disabled className="font-semibold text-xs uppercase text-muted-foreground">
                          — {group.label} —
                        </SelectItem>,
                        ...Object.entries(group.statuses).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))
                      ]);
                    }
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportModalOpen(false)}>Cancel</Button>
            <Button onClick={handleExportCSV} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Won + Intake Confirmation Modal */}
      <Dialog open={wonIntakeModalOpen} onOpenChange={setWonIntakeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {wonIntakeData.isResend ? "Resend Intake Form" : "Mark as Won & Start Intake?"}
            </DialogTitle>
            <DialogDescription>
              {wonIntakeData.isResend 
                ? "Send a new intake link to the client. Previous links will be invalidated."
                : "This will move the opportunity into the Intake workflow."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Send Email Toggle - hidden for resend (always sending) */}
            {!wonIntakeData.isResend && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Send intake email now</Label>
                  <p className="text-sm text-muted-foreground">
                    Email the client with intake form link
                  </p>
                </div>
                <Checkbox
                  checked={wonIntakeData.sendEmail}
                  onCheckedChange={(checked) => 
                    setWonIntakeData(prev => ({ ...prev, sendEmail: checked }))
                  }
                />
              </div>
            )}
            
            {/* Form Selector - shown when sendEmail is ON or isResend */}
            {(wonIntakeData.sendEmail || wonIntakeData.isResend) && (
              <>
                <div className="space-y-2">
                  <Label>Intake Form</Label>
                  <Select 
                    value={wonIntakeData.formId} 
                    onValueChange={(v) => setWonIntakeData(prev => ({ ...prev, formId: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select intake form" />
                    </SelectTrigger>
                    <SelectContent>
                      {intakeForms.map(form => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {intakeForms.length === 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-amber-600">
                        No intake forms available. Create one in Form Builder first.
                      </p>
                      {/* Debug info - remove after confirming fix */}
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer">Debug Info</summary>
                        <pre className="mt-1 p-2 bg-muted rounded text-[10px]">
                          Endpoint: /api/business/forms{'\n'}
                          Forms returned: {intakeForms.length}{'\n'}
                          Looking for: mode=post_booking, status!=draft
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Recipient Email</Label>
                  <Input
                    type="email"
                    value={wonIntakeData.recipientEmail}
                    onChange={(e) => setWonIntakeData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                    placeholder="client@example.com"
                  />
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setWonIntakeModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmWonIntake} 
              disabled={wonIntakeLoading || ((wonIntakeData.sendEmail || wonIntakeData.isResend) && (!wonIntakeData.formId || !wonIntakeData.recipientEmail))}
            >
              {wonIntakeLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {wonIntakeData.isResend ? "Send Email" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Intake (Intake-First) Modal */}
      <Dialog open={startIntakeModalOpen} onOpenChange={setStartIntakeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start Intake</DialogTitle>
            <DialogDescription>
              Create a new intake request for a client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Contact Selection */}
            <div className="space-y-2">
              <Label>Contact</Label>
              <div className="flex items-center gap-2 mb-2">
                <Checkbox
                  id="createNewContact"
                  checked={startIntakeData.createNewContact}
                  onCheckedChange={(checked) => 
                    setStartIntakeData(prev => ({ 
                      ...prev, 
                      createNewContact: checked,
                      contactId: "",
                      name: "",
                      email: ""
                    }))
                  }
                />
                <label htmlFor="createNewContact" className="text-sm">
                  Create new contact
                </label>
              </div>
              
              {!startIntakeData.createNewContact ? (
                <Select 
                  value={startIntakeData.contactId} 
                  onValueChange={(v) => {
                    const contact = contacts.find(c => c.id === v);
                    setStartIntakeData(prev => ({ 
                      ...prev, 
                      contactId: v,
                      name: contact?.name || "",
                      email: contact?.email || ""
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select existing contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(contact => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} ({contact.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={startIntakeData.name}
                      onChange={(e) => setStartIntakeData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Client name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={startIntakeData.email}
                      onChange={(e) => setStartIntakeData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="client@example.com"
                    />
                  </div>
                </>
              )}
            </div>
            
            {/* Form Selector */}
            <div className="space-y-2">
              <Label>Intake Form *</Label>
              <Select 
                value={startIntakeData.formId} 
                onValueChange={(v) => setStartIntakeData(prev => ({ ...prev, formId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select intake form" />
                </SelectTrigger>
                <SelectContent>
                  {intakeForms.map(form => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {intakeForms.length === 0 && (
                <p className="text-sm text-amber-600">
                  No intake forms available. Create one in Form Builder first.
                </p>
              )}
            </div>
            
            {/* Send Email Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Send intake email now</Label>
                <p className="text-sm text-muted-foreground">
                  Email will be sent immediately
                </p>
              </div>
              <Checkbox
                checked={startIntakeData.sendEmail}
                onCheckedChange={(checked) => 
                  setStartIntakeData(prev => ({ ...prev, sendEmail: checked }))
                }
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setStartIntakeModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitStartIntake} 
              disabled={startIntakeLoading || !startIntakeData.formId || !startIntakeData.email || (startIntakeData.createNewContact && !startIntakeData.name)}
            >
              {startIntakeLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert from Won Warning Modal */}
      <Dialog open={revertWarningOpen} onOpenChange={setRevertWarningOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Intake Already Sent
            </DialogTitle>
            <DialogDescription>
              An intake email has already been sent for this opportunity. 
              Reverting the status will not unsend the email — the client may 
              still submit their intake form.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to change the status back to <strong>{revertData.newStatus ? leadStatusConfig[revertData.newStatus]?.label : ''}</strong>?
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRevertWarningOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRevert}>
              Yes, Change Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lost Reason Modal */}
      <Dialog open={lostReasonModalOpen} onOpenChange={setLostReasonModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-gray-500" />
              Why was this lead lost?
            </DialogTitle>
            <DialogDescription>
              Help improve your sales process by tracking why leads don't convert.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            {[
              { value: "not_a_fit", label: "Not a fit" },
              { value: "price", label: "Price" },
              { value: "no_response", label: "No response" },
              { value: "chose_competitor", label: "Chose competitor" },
              { value: "other", label: "Other" }
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  lostReasonData.reason === option.value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`lost-reason-${option.value}`}
              >
                <input
                  type="radio"
                  name="lost_reason"
                  value={option.value}
                  checked={lostReasonData.reason === option.value}
                  onChange={(e) => setLostReasonData(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
            
            {lostReasonData.reason === "other" && (
              <div className="pt-2">
                <Input
                  placeholder="Please specify..."
                  value={lostReasonData.otherText}
                  onChange={(e) => setLostReasonData(prev => ({ ...prev, otherText: e.target.value }))}
                  className="rounded-xl"
                  data-testid="lost-reason-other-input"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setLostReasonModalOpen(false);
                setLostReasonData({ submissionId: null, reason: "", otherText: "" });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmLostReason}
              disabled={!lostReasonData.reason || lostReasonSaving || (lostReasonData.reason === "other" && !lostReasonData.otherText.trim())}
              data-testid="confirm-lost-reason"
            >
              {lostReasonSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Composer Modal (from Submission Drawer) */}
      <EmailComposerModal
        open={emailComposerState.open}
        onClose={() => setEmailComposerState({ open: false, submission: null, mode: "compose", templateId: null })}
        submission={emailComposerState.submission}
        contact={null}
        token={token}
        templates={[
          ...(emailTemplates.pre_booking || []),
          ...(emailTemplates.post_booking || [])
        ]}
        businessName={user?.business_name || ""}
        mode={emailComposerState.mode}
        selectedTemplateId={emailComposerState.templateId}
        onEmailSent={() => {
          // Refresh submission to update activity timeline
          fetchData();
          toast.success("Email sent and logged to activity timeline");
        }}
      />
    </div>
  );
}
